import React, { useContext, useState } from "react";
import NVHorizontalBarChart, {
  formats,
} from "common/graphs/NVHorizontalBarChart";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import { LegendItem } from "../utils";

const AllBqnsWrapper = styled.div`
  .horizontal-bars-wrapper {
    margin: 0;
  }

  h6 {
    margin: 20px 0 -20px 0;
  }

  .horizontal-bars-wrapper {
  }
`;

const UsageGraphLayout = styled.div`
  h4 {
    text-align: center;
    &.align-left {
      text-align: left;
    }
  }
  .centered {
    text-align: center;
  }
  .horizontal-bars-wrapper {
    .legends {
      display: flex;
      justify-content: flex-end;
      ul {
        margin-right: -30px;
      }
      li {
        margin: 0;
      }
    }
    h6 {
      margin: 0 0 -20px 0;
    }
  }
`;

const LegendsWrapper = styled.div`
  margin: 10px 0 20px;
  display: flex;
  justify-content: flex-end;
`;

const extractFacet = (field, result, mode) => {
  const values = [];
  for (const [facet, data] of Object.entries(result)) {
    if (mode !== "standalone") {
      values.push(
        ...data.map((item) => ({
          label: `${facet}`,
          value: item[field],
        }))
      );
    } else {
      values.push({
        label: facet,
        value: data[field],
      });
    }
  }
  return values;
};

const orderMap = {
  Free: 0,
  Used: 1,
};

const usedAndFreeFrom = ({ result, colors, mode, selection }) => {
  const items = [...selection].map((field) => {
    return {
      key: field,
      color: colors[field],
      order: orderMap[field],
      values: extractFacet(field.toLowerCase(), result, mode),
    };
  });
  return [...items].sort((a, b) => b.order - a.order);
};

const getFilteredResult = (result, bqnId, mode) => {
  if (!result || mode === "standalone") {
    return result;
  }
  return Object.entries({ ...result }).reduce((acc, [key, value]) => {
    const filteredValue = Array.isArray(value)
      ? value.filter((item) => {
          return parseFloat(item.bqnId) === parseFloat(bqnId);
        })
      : value;
    acc[key] = filteredValue;
    return acc;
  }, {});
};

const fields = ["Used", "Free"];
const usedColor = "#006AB7";

const Result = ({ ...result }) => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const [selection, setSelection] = useState(new Set([...fields]));
  const freeColor = `${login.isTheme("light") ? "#cacaca" : "#3A3A3E"}`;
  const colors = { Free: freeColor, Used: usedColor };

  const handleLegendClick = (label) => {
    if (selection.has(label)) {
      const newSelection = [...selection].filter((item) => item !== label);
      setSelection(new Set(newSelection));
    } else {
      const newSelection = selection.add(label);
      setSelection(new Set([...newSelection]));
    }
  };

  const selectionLabel = [...selection].join("-");
  if (bqnId === "all") {
    const bqnsActive = Object.entries(result).reduce((acc, [name, values])=> {
      const bqns = values.map(({bqnId})=>parseFloat(bqnId));
    return [...acc, ...bqns];
    }, [])
    const bqnsSet = new Set(bqnsActive);
    const bqns = [...bqnsSet];
    return (
      <AllBqnsWrapper>
        <LegendsWrapper>
          {fields.map((field, index) => {
            return (
              <LegendItem
                label={field}
                field={field}
                color={colors[field]}
                key={field}
                isSelected={selection.has(field)}
                onSelection={handleLegendClick}
              />
            );
          })}
        </LegendsWrapper>
        {bqns.map((bqnId) => {
          const filteredResult = getFilteredResult(result, bqnId, mode);
          const items = usedAndFreeFrom({
            result: filteredResult,
            mode,
            colors,
            selection,
          });

          return (
            <>
              <h6>{`BQN ${bqnId}`}</h6>
              <UsageGraphLayout>
                <NVHorizontalBarChart
                  animationDuration={0}
                  hideLegends={true}
                  id={`horizontal-bar-chart-${bqnId}-${selectionLabel}`}
                  key={`horizontal-bar-chart-${bqnId}-${selectionLabel}`}
                  valueUnits=""
                  tooltipValueFormatter={(value) =>
                    `${formats.threeDecimals(value)} GB`
                  }
                  valueFormat={formats.threeDecimals}
                  items={items}
                  showRoundCorners={true}
                  showColors={true}
                  barHeight={45}
                  margin={{ top: 30, right: 50, bottom: 30, left: 100 }}
                />
                <div className="centered">Gbytes</div>
              </UsageGraphLayout>
            </>
          );
        })}
      </AllBqnsWrapper>
    );
  }

  return (
    <UsageGraphLayout>
      <NVHorizontalBarChart
        key={`horizontal-bar-chart-${bqnId}`}
        valueUnits=""
        tooltipValueFormatter={(value) => `${formats.threeDecimals(value)} GB`}
        valueFormat={formats.threeDecimals}
        items={usedAndFreeFrom({
          result: getFilteredResult(result, bqnId, mode),
          mode,
          colors,
          selection,
        })}
        showRoundCorners={true}
        showColors={true}
        margin={{ top: 30, right: 50, bottom: 30, left: 100 }}
        title={mode === "standalone" ? undefined : `BQN ${bqnId}`}
      />
      <div className="centered">Gbytes</div>
    </UsageGraphLayout>
  );
};

export default Result;
